import React, { useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Card, CardContent, Chip, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, Stack, Tooltip, Typography,
} from '@mui/material';
import { Add, Delete, Edit as EditIcon, Send } from '@mui/icons-material';
import api from '../services/api';
import TextField from '../components/common/TextField';

type ConfigEvent = { key: string; label: string };
type ConfigGroup = { id: number; name: string; members: { userId?: number | null; email?: string | null; userName?: string | null }[] };
type UserOption = { id: number; name: string; email: string };
// Un integrante en el editor: un usuario del sistema o un email externo tipeado.
type MemberOption = UserOption | string;

const memberLabel = (m: MemberOption) => (typeof m === 'string' ? m : `${m.name} (${m.email})`);

export const NotificationSettingsPage: React.FC = () => {
  const [events, setEvents] = useState<ConfigEvent[]>([]);
  const [groups, setGroups] = useState<ConfigGroup[]>([]);
  const [subs, setSubs] = useState<Record<string, number[]>>({});
  const [shortExpiryMonths, setShortExpiryMonths] = useState(8);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const [groupDialog, setGroupDialog] = useState<{ id: number | null; name: string; members: MemberOption[] } | null>(null);

  const load = async () => {
    try {
      const cfg = await api.getNotificationConfig();
      setEvents(cfg.events || []);
      setGroups(cfg.groups || []);
      setSubs(Object.fromEntries((cfg.subscriptions || []).map(s => [s.eventKey, s.groupIds])));
      setShortExpiryMonths(cfg.shortExpiryMonths || 8);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo cargar la configuración');
    }
  };

  useEffect(() => {
    load();
    api.getUsers().then(r => setUsers((r.items || []).map((u: any) => ({ id: u.id, name: u.name, email: u.email })).filter((u: UserOption) => !!u.email)))
      .catch(() => { /* sin usuarios no se puede elegir, pero se pueden tipear emails */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openGroupDialog = (g?: ConfigGroup) => {
    setError(null); setSuccess(null);
    if (!g) { setGroupDialog({ id: null, name: '', members: [] }); return; }
    const members: MemberOption[] = g.members.map(m => {
      const user = m.userId ? users.find(u => u.id === m.userId) : undefined;
      return user || (m.email || '');
    }).filter(m => (typeof m === 'string' ? m.length > 0 : true));
    setGroupDialog({ id: g.id, name: g.name, members });
  };

  const saveGroup = async () => {
    if (!groupDialog || !groupDialog.name.trim()) return;
    const payload = {
      name: groupDialog.name.trim(),
      members: groupDialog.members.map(m => (typeof m === 'string' ? { email: m } : { userId: m.id })),
    };
    setSaving(true);
    try {
      if (groupDialog.id) await api.updateNotificationGroup(groupDialog.id, payload);
      else await api.createNotificationGroup(payload);
      setGroupDialog(null);
      await load();
      setSuccess('Grupo guardado');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo guardar el grupo');
    } finally {
      setSaving(false);
    }
  };

  const deleteGroup = async (g: ConfigGroup) => {
    if (!window.confirm(`¿Eliminar el grupo "${g.name}"? Se quita también de los eventos suscriptos.`)) return;
    try {
      await api.deleteNotificationGroup(g.id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo eliminar el grupo');
    }
  };

  const saveSubsAndSettings = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.saveNotificationSubscriptions(events.map(e => ({ eventKey: e.key, groupIds: subs[e.key] || [] })));
      await api.saveNotificationSettings({ shortExpiryMonths });
      setSuccess('Configuración guardada');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    setError(null); setSuccess(null);
    try {
      const res = await api.sendNotificationTest();
      if (res.sent) setSuccess(`Email de prueba enviado a ${res.to}`);
      else setError(`No se pudo enviar a ${res.to}: ${res.message}`);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo enviar el email de prueba');
    } finally {
      setTesting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }}>
        <Typography variant="h4" fontWeight={700}>Notificaciones</Typography>
        <Button startIcon={<Send />} variant="outlined" onClick={sendTest} disabled={testing}>
          {testing ? 'Enviando…' : 'Enviar mail de prueba'}
        </Button>
      </Stack>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      <Card>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="h6">Grupos</Typography>
            <Button startIcon={<Add />} variant="contained" size="small" onClick={() => openGroupDialog()}>Nuevo grupo</Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Un grupo junta a las personas que reciben los avisos. Después, en cada evento elegís qué grupos se notifican.
          </Typography>
          {groups.length === 0 && <Typography variant="body2" color="text.secondary">Todavía no hay grupos.</Typography>}
          <Stack spacing={1}>
            {groups.map(g => (
              <Stack key={g.id} direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                <Typography fontWeight={600} sx={{ minWidth: 140 }}>{g.name}</Typography>
                {g.members.length === 0 && <Typography variant="body2" color="text.secondary">Sin integrantes</Typography>}
                {g.members.map((m, i) => (
                  <Chip key={i} size="small" label={m.userName ? `${m.userName}` : m.email} title={m.email || ''} />
                ))}
                <Tooltip title="Editar grupo"><IconButton size="small" onClick={() => openGroupDialog(g)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Eliminar grupo"><IconButton size="small" color="error" onClick={() => deleteGroup(g)}><Delete fontSize="small" /></IconButton></Tooltip>
              </Stack>
            ))}
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>Eventos</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Para cada evento, elegí qué grupos reciben el aviso por email. Un evento sin grupos no manda nada.
          </Typography>
          <Stack spacing={2}>
            {events.map(ev => (
              <Stack key={ev.key} direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                <Typography sx={{ minWidth: 340 }}>{ev.label}</Typography>
                <Autocomplete
                  multiple
                  size="small"
                  options={groups}
                  getOptionLabel={g => g.name}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={groups.filter(g => (subs[ev.key] || []).includes(g.id))}
                  onChange={(_, v) => setSubs(prev => ({ ...prev, [ev.key]: v.map(g => g.id) }))}
                  renderInput={params => <TextField {...params} placeholder={groups.length ? 'Elegir grupos' : 'Creá un grupo primero'} />}
                  sx={{ flex: 1, minWidth: 260 }}
                />
              </Stack>
            ))}
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>Alarma de vencimiento corto</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Al recibir mercadería, si algún lote vence antes de este plazo se avisa en pantalla y se manda el evento
            "vencimiento corto" con prioridad alta.
          </Typography>
          <TextField
            label="Umbral (meses)"
            type="number"
            size="small"
            value={shortExpiryMonths}
            onChange={e => setShortExpiryMonths(Math.max(1, Math.min(60, Number(e.target.value) || 1)))}
            inputProps={{ min: 1, max: 60 }}
            sx={{ width: 180 }}
          />
        </CardContent>
      </Card>

      <Box>
        <Button variant="contained" onClick={saveSubsAndSettings} disabled={saving}>
          {saving ? 'Guardando…' : 'Guardar configuración'}
        </Button>
      </Box>

      <Dialog open={!!groupDialog} onClose={() => setGroupDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{groupDialog?.id ? 'Editar grupo' : 'Nuevo grupo'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              autoFocus
              label="Nombre del grupo"
              placeholder="Ej. Calidad, Logística, Dirección"
              value={groupDialog?.name || ''}
              inputProps={{ maxLength: 100 }}
              onChange={e => setGroupDialog(d => (d ? { ...d, name: e.target.value } : d))}
              fullWidth
            />
            <Autocomplete
              multiple
              freeSolo
              options={users}
              getOptionLabel={memberLabel}
              value={groupDialog?.members || []}
              onChange={(_, v) => setGroupDialog(d => (d ? { ...d, members: v as MemberOption[] } : d))}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip {...getTagProps({ index })} size="small" label={typeof option === 'string' ? option : option.name} title={typeof option === 'string' ? option : option.email} />
                ))
              }
              renderInput={params => (
                <TextField {...params} label="Integrantes" placeholder="Elegí usuarios o tipeá un email externo y Enter" />
              )}
            />
            <Typography variant="caption" color="text.secondary">
              Los usuarios reciben en el email de su cuenta. También podés agregar emails externos sueltos.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupDialog(null)}>Cancelar</Button>
          <Button variant="contained" onClick={saveGroup} disabled={saving || !groupDialog?.name.trim()}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotificationSettingsPage;
